import React from 'react';
import moment from 'jalali-moment';
import { Link, Redirect } from 'react-router-dom';
import Dialog from '../../toolkit/dialog';
import SensorDialog from './SensorDialog';
import Logger from '../../toolkit/logger/Logger';
import { NodeType } from '../widgets/types';
import { traverseChildren } from '../../utils/easy';

import './NodeItem.scss';

export interface NodeItemProps {
  id: string;
  name: string | React.ReactElement;
  type: NodeType;
  sensor?: any;
  onClick?: (id: string) => void;
}

export interface FloatNodeItemProps {
  id: string;
  name: string | React.ReactElement;
  type: string;
  top: number | string;
  left: number | string;
  wrapperElement: HTMLDivElement;
  margin?: string;
  containerWidth?: string;
}

const icon = (type: NodeType) => {
  let classes = type + ' ';
  switch (type) {
    case 'group':
      classes += 'fas fa-folder';
      break;
    case 'sensor':
      classes += 'far fa-sensor';
      break;
  }
  return <i className={classes}></i>;
};

const sensorMeta = (sensor: any) => {
  const hasLog = sensor.log != null;
  const hasLogData = hasLog && sensor.log.data != null;
  const hasLogDateTime = hasLog && sensor.log.dateTime != null;
  const momentInstance = moment.from(sensor.log.dateTime, 'en').subtract(210, 'minutes');

  return (
    <div className="meta-container">
      <div className="text-center">{sensor._id}</div>
      <hr />
      <div className="row mt-2">
        <div className="col-6 text-center">
          <span>آخرین زمان:</span>
          <span className="ltr text-left">
            {
              (hasLogDateTime) ?
                momentInstance.format('jYY/jMM/jDD HH:mm:ss') :
                'نامشخص'
            }
          </span>
        </div>
        <div className="col-6 text-center">
          <span>آخرین دیتا:</span>
          <span className="ltr text-left">
            {(hasLogData) ? sensor.log.data : 'نامشخص'}
          </span>
        </div>
      </div>
    </div>
  );
}

export const NodeItem = (props: NodeItemProps) => {
  const onClick = props.onClick ?? (() => { });
  const nodeContent = (
    <>
      {icon(props.type)}
      <span className="node-name">
        {props.name}
      </span>
      {props.type === 'group' ? '' : sensorMeta(props.sensor)}
    </>
  )
  const colClasses = 'col-lg-3 col-md-4 col-sm-6 col-12 mb-3';
  if (props.type === 'group') {
    return (
      <div className={colClasses}>
        <Link to={`/dashboard/groups/${props.id}`} className="node-item">
          {nodeContent}
        </Link>
      </div>
    );
  }
  return (
    <div
      className={colClasses}
      onClick={onClick.bind(this, props.id)}>
      <div className="node-item">
        {nodeContent}
      </div>
    </div>
  );
}

export class FloatNodeItem extends React.Component<FloatNodeItemProps> {

  state = {
    moving: false,
    top: this.props.top,
    left: this.props.left,
    redirect: null
  }

  private startupX: number;
  private startupY: number;

  private elementRef = React.createRef<any>();
  private longPressTimeout: NodeJS.Timeout;
  private preventClick = false;

  render() {
    const { top, left, moving, redirect } = this.state;
    if (redirect) {
      return <Redirect to={redirect} push />;
    }
    const { type } = this.props;

    const attrs = {
      to: '/dashboard/groups/' + this.props.id,
      ref: this.elementRef,
      onMouseDown: this.handleMouseDown,
      onTouchStart: this.handleMouseDown as any,
      onTouchEnd: this.handleMouseDown as any,
      className: 'float-node-item' + (moving ? ' moving' : ''),
      style: { left, top }
    }

    if (type === 'group') {
      return (
        <Link {...attrs} onClick={this.handleGroupClick} data-id={this.props.id} data-type="group">
          <div>{icon(type as any)}</div>
          <div className="node-name">{/* {name} */}</div>
        </Link>
      )
    }
    return (
      <div {...attrs} onClick={this.handleSensorClick} data-id={this.props.id} data-type="sensor">
        <div>{icon(type as any)}</div>
        <div className="node-name">{/* {name} */}</div>
      </div>
    )
  }

  componentDidMount = () => {
    Logger.info('FloatNode::componentDidMount');
    document.addEventListener('mouseup', this.handleMouseUp as any);
  }

  componentWillUnmount = () => {
    Logger.info('FloatNode::componentWillUnmount');
    document.removeEventListener('mouseup', this.handleMouseUp as any);
    document.removeEventListener('mousemove', this.handleMouseMove as any);
  }

  handleLongPress = () => {
    this.preventClick = true;
    Logger.info('FloatNode::LogPressEvent::EnableMove');
    this.setState({ moving: true });
  }

  handleMouseOut = (event: React.MouseEvent) => {
    const list = traverseChildren(event.currentTarget as HTMLElement);
    var e = (event as any).toElement || event.relatedTarget;
    if (!!~list.indexOf(e)) {
      return;
    }
    this.handleMouseDown(event);
  }

  handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
    Logger.info('FloatNode::MouseDownEvent::ClientPosition', event.clientX, event.clientY);
    this.startupX = event.clientX;
    this.startupY = event.clientY;
    this.longPressTimeout = setTimeout(() => {
      document.addEventListener('mousemove', this.handleMouseMove as any);
      this.handleLongPress();
    }, 500);
  }

  handleMouseUp = (event: React.MouseEvent) => {
    event.preventDefault();
    Logger.info('FloatNode::MouseUpEvent call');
    clearTimeout(this.longPressTimeout);
    document.removeEventListener('mousemove', this.handleMouseMove as any);
    this.setState({ moving: false });
    return false;
  }

  handleMouseMove = (event: React.MouseEvent) => {
    const { moving } = this.state;
    const { clientX, clientY } = event;
    clearInterval(this.longPressTimeout);
    if (moving) {
      const element = this.elementRef.current;
      const valueX = this.startupX - clientX;
      const valueY = this.startupY - clientY;
      this.startupX = clientX;
      this.startupY = clientY;
      const result = this.calculatePositions(
        element.offsetTop - valueY,
        element.offsetLeft - valueX
      );

      if (clientX < result.left || clientY < result.top) {
        Logger.info('FloatNode::MouseMove::DisableMove');
        this.setState({ moving: false });
      }
      Logger.info('FloatNode::MouseMove::UpdatePosition', result);
      this.setState({ left: result.leftPercent, top: result.topPercent });
    }
  }

  handleGroupClick = (event: React.MouseEvent) => {
    if (this.preventClick) {
      event.preventDefault();
      this.preventClick = false;
      return;
    }
  }

  handleSensorClick = (event: React.MouseEvent) => {
    if (this.preventClick) {
      event.preventDefault();
      this.preventClick = false;
      return;
    }
    Logger.info('FloatNode::MouseEvent::SensorClick');
    const dialog = new Dialog({
      rtl: true,
      size: 'small',
      showTitle: false,
      message: SensorDialog,
      onResolve: path => {
        this.setState({ redirect: path });
        dialog.close();
      }

    });
    dialog.show({ id: this.props.id });
  }

  private calculatePositions = (top: number, left: number) => {
    const { wrapperElement: wrapper } = this.props;
    const wrapperH = wrapper.offsetHeight;
    const wrapperW = wrapper.offsetWidth;
    const element = this.elementRef.current;

    const maxTop = wrapperH - element.clientHeight - 1;
    const maxLeft = wrapperW - element.clientWidth - 1;

    if (left < 1) {
      left = 1;
    }
    if (top < 1) {
      top = 1;
    }
    if (top > maxTop) {
      top = maxTop;
    }
    if (left > maxLeft) {
      left = maxLeft;
    }

    const leftPercent = (100 * left / wrapperW).toPrecision(6) + '%';
    const topPercent = (100 * top / wrapperH).toPrecision(6) + '%';
    return { top, left, topPercent, leftPercent };
  }

}
